import axios from 'axios';
import { User } from '../types';

// API base URL - configured based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://backend-929516280881.us-central1.run.app'  // Direct Cloud Run URL
  : 'http://localhost:8000';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add interceptor to include auth token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Authentication API calls
export const login = async (username: string, password: string) => {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);
  
  const response = await axios.post(`${API_BASE_URL}/token`, formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  });
  return response.data;
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await api.get('/users/me');
  return response.data;
};

// Dashboard API calls
export const getEtlStatus = async () => {
  const response = await api.get('/dashboard/etl-status');
  return response.data;
};

export const getSalesSummary = async (startDate?: string, endDate?: string, storeId?: string) => {
  let url = '/dashboard/sales-summary';
  const params: Record<string, string> = {};
  
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (storeId) params.store_id = storeId;
  
  const response = await api.get(url, { params });
  return response.data;
};

export const getProductPerformance = async (month?: string) => {
  let url = '/dashboard/product-performance';
  const params: Record<string, string> = {};
  
  if (month) params.month = month;
  
  const response = await api.get(url, { params });
  return response.data;
};

export const getStorePerformance = async (month?: string, storeId?: string) => {
  let url = '/dashboard/store-performance';
  const params: Record<string, string> = {};
  
  if (month) params.month = month;
  if (storeId) params.store_id = storeId;
  
  const response = await api.get(url, { params });
  return response.data;
};

export const getInventory = async (storeId?: string, lowStockOnly?: boolean) => {
  let url = '/dashboard/inventory';
  const params: Record<string, string | boolean> = {};
  
  if (storeId) params.store_id = storeId;
  if (lowStockOnly) params.low_stock_only = lowStockOnly;
  
  const response = await api.get(url, { params });
  return response.data;
};

export const getTransfers = async (
  startDate?: string, 
  endDate?: string, 
  originStore?: string, 
  destinationStore?: string
) => {
  let url = '/dashboard/transfers';
  const params: Record<string, string> = {};
  
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (originStore) params.origin_store = originStore;
  if (destinationStore) params.destination_store = destinationStore;
  
  const response = await api.get(url, { params });
  return response.data;
};

export const getStores = async () => {
  const response = await api.get('/dashboard/stores');
  return response.data;
};

export const getVendedorDetalhado = async (codVendedor: number, dataInicio?: string, dataFim?: string, lojaId?: number) => {
  const params: any = {};
  if (dataInicio) params.data_inicio = dataInicio;
  if (dataFim) params.data_fim = dataFim;
  if (lojaId) params.loja_id = lojaId;
  
  const response = await api.get(`/analytics/vendedor-detalhado/${codVendedor}`, { params });
  return response.data;
};

export const getAvailableMonths = async () => {
  const response = await api.get('/dashboard/months');
  return response.data;
};

// Analytics API calls - New BI System
export const getKpisPrincipais = async (periodo: string = '30d', lojaId?: number, customDates?: { startDate: string; endDate: string }) => {
  const params: Record<string, string | number> = { periodo };
  if (lojaId) params.loja_id = lojaId;
  if (customDates && periodo === 'custom') {
    params.start_date = customDates.startDate;
    params.end_date = customDates.endDate;
  }
  
  const response = await api.get('/analytics/kpis-principais', { params });
  return response.data;
};

export const getVendasTempoReal = async (periodo: string = '7d', lojaId?: number, customDates?: { startDate: string; endDate: string }) => {
  const params: Record<string, string | number> = { periodo };
  if (lojaId) params.loja_id = lojaId;
  if (customDates && periodo === 'custom') {
    params.start_date = customDates.startDate;
    params.end_date = customDates.endDate;
  }
  
  const response = await api.get('/analytics/vendas-tempo-real', { params });
  return response.data;
};

export const getPerformanceLojas = async (periodo: string = '30d', metrica: string = 'receita', customDates?: { startDate: string; endDate: string }) => {
  const params: Record<string, string | number> = { periodo, metrica };
  if (customDates && periodo === 'custom') {
    params.start_date = customDates.startDate;
    params.end_date = customDates.endDate;
  }
  const response = await api.get('/analytics/performance-lojas', { params });
  return response.data;
};

export const getAnaliseProdutos = async (periodo: string = '30d', categoria?: string, lojaId?: number, customDates?: { startDate: string; endDate: string }) => {
  const params: Record<string, string | number> = { periodo };
  if (categoria) params.categoria = categoria;
  if (lojaId) params.loja_id = lojaId;
  if (customDates && periodo === 'custom') {
    params.start_date = customDates.startDate;
    params.end_date = customDates.endDate;
  }
  
  const response = await api.get('/analytics/analise-produtos', { params });
  return response.data;
};

export const getTendenciasVendas = async (periodo: string = '90d', granularidade: string = 'dia', customDates?: { startDate: string; endDate: string }) => {
  const params: Record<string, string | number> = { periodo, granularidade };
  if (customDates && periodo === 'custom') {
    params.start_date = customDates.startDate;
    params.end_date = customDates.endDate;
  }
  const response = await api.get('/analytics/tendencias-vendas', { params });
  return response.data;
};

export const getDashboardPrincipal = async (dias: number = 90) => {
  const params = { dias };
  const response = await api.get('/analytics/dashboard-principal', { params });
  return response.data;
};

export const getKpisGerais = async () => {
  const response = await api.get('/analytics/kpis-gerais');
  return response.data;
};

// =================== FASE 5 - DASHBOARDS AVANÇADOS ===================

/**
 * Performance por Vendedor
 * Análise completa de vendedores individuais
 */
export const getPerformanceVendedores = async (periodo: string = '30d', lojaId?: number, customDates?: { startDate: string; endDate: string }) => {
  const params: Record<string, any> = { periodo };
  if (lojaId) params.loja_id = lojaId;
  if (customDates && periodo === 'custom') {
    params.start_date = customDates.startDate;
    params.end_date = customDates.endDate;
  }
  
  const response = await api.get('/analytics/performance-vendedores', { params });
  return response.data;
};


/**
 * Vendas Externas
 * Dashboard específico para vendas externas
 */
export const getVendasExternas = async (periodo: string = '30d', lojaId?: number, customDates?: { startDate: string; endDate: string }) => {
  const params: Record<string, any> = { periodo };
  if (lojaId) params.loja_id = lojaId;
  if (customDates && periodo === 'custom') {
    params.start_date = customDates.startDate;
    params.end_date = customDates.endDate;
  }
  
  const response = await api.get('/analytics/vendas-externas', { params });
  return response.data;
};

/**
 * Vendas Externas Mensal
 * Agregação mensal de vendas externas com separação por tipo
 */
export const getVendasExternasMensal = async (lojaId?: number) => {
  const params: Record<string, any> = {};
  if (lojaId) params.loja_id = lojaId;
  
  const response = await api.get('/analytics/vendas-externas-mensal', { params });
  return response.data;
};

/**
 * Loja Detalhado (Drill-down)
 * Drill-down completo por loja específica
 */
export const getLojaDetalhado = async (lojaId: number, periodo: string = '30d') => {
  const params = { periodo };
  const response = await api.get(`/analytics/loja-detalhado/${lojaId}`, { params });
  return response.data;
};

// =================== FASE 2 - NOVOS ENDPOINTS MULTIDIMENSIONAIS ===================

/**
 * Performance Diária por Loja
 * NEW Tarefa 2.2: Drill-down diário com top vendedor
 */
export const getPerformanceLojaDiaria = async (
  lojaId: number,
  dataInicio?: string,
  dataFim?: string
) => {
  const params: Record<string, string> = {};
  if (dataInicio) params.data_inicio = dataInicio;
  if (dataFim) params.data_fim = dataFim;
  
  const response = await api.get(`/analytics/performance-lojas/${lojaId}/diaria`, { params });
  return response.data;
};

/**
 * Vendedores por Loja
 * NEW Tarefa 2.3: Ranking de vendedores em loja específica
 */
export const getVendedoresLoja = async (
  lojaId: number,
  periodo: string = '30d',
  ordenacao: string = 'receita'
) => {
  const params = { periodo, ordenacao };
  const response = await api.get(`/analytics/performance-lojas/${lojaId}/vendedores`, { params });
  return response.data;
};


/**
 * Análise Mensal Detalhada
 * Retorna análise completa de um mês específico
 */
export const getAnaliseMensal = async (month: string) => {
  const response = await api.get(`/analytics/analise-mensal/${month}`);
  return response.data;
};

/**
 * Vendas Mensais - Últimos 12 meses
 * Retorna dados agregados mensais para tabela de vendas
 */
export const getVendasMensais = async (lojaId?: number) => {
  const params: Record<string, any> = {};
  if (lojaId) params.loja_id = lojaId;
  
  const response = await api.get('/analytics/vendas-mensais', { params });
  return response.data;
};

/**
 * Todas as Vendas Mensais - Histórico completo
 * Retorna dados agregados mensais de todos os meses disponíveis
 */
export const getAllVendasMensais = async (lojaId?: number, page?: number, limit?: number) => {
  const params: Record<string, any> = {};
  if (lojaId) params.loja_id = lojaId;
  if (page !== undefined) params.page = page;
  if (limit !== undefined) params.limit = limit;
  
  const response = await api.get('/analytics/all-vendas-mensais', { params });
  return response.data;
};

/**
 * Vendas Diárias - Detalhamento por mês
 * Retorna dados diários para um mês específico
 */
export const getVendasDiarias = async (year: number, month: string) => {
  const response = await api.get(`/analytics/vendas-diarias/${year}/${month}`);
  return response.data;
};

/**
 * Análise de Formas de Pagamento
 * Retorna análise detalhada de pagamentos por loja
 */
export const getFormasPagamento = async (
  periodo: string = '30d',
  lojaId?: number,
  dataInicio?: string,
  dataFim?: string
) => {
  const params: Record<string, any> = { periodo };
  if (lojaId) params.loja_id = lojaId;
  if (dataInicio) params.data_inicio = dataInicio;
  if (dataFim) params.data_fim = dataFim;
  
  const response = await api.get('/analytics/formas-pagamento', { params });
  return response.data;
};

/**
 * Análise de Pagamento por Vendedor
 * Retorna análise de formas de pagamento por vendedor
 */
export const getPagamentoVendedor = async (
  lojaId: number,
  periodo: string = '30d'
) => {
  const params = { periodo };
  const response = await api.get(`/analytics/lojas/${lojaId}/pagamento-vendedor`, { params });
  return response.data;
};

/**
 * Totais de Produtos por Tipo
 * Retorna totais agregados de produtos por tipo
 */
export const getProductTotals = async (
  periodo: string = '30d',
  lojaId?: number
) => {
  const params: Record<string, any> = { periodo };
  if (lojaId) params.loja_id = lojaId;
  
  const response = await api.get('/analytics/produtos/totais', { params });
  return response.data;
};

/**
 * Análise de Produtos
 * Retorna dados de produtos por tipo (todos, ponta, promocao, normal)
 */
export const getProductData = async (
  periodo: string = '30d',
  lojaId?: number,
  tipo?: 'todos' | 'ponta' | 'promocao' | 'normal'
) => {
  const params: Record<string, any> = { periodo };
  if (lojaId) params.loja_id = lojaId;
  if (tipo) params.tipo = tipo;
  
  const response = await api.get('/analytics/produtos', { params });
  return response.data;
};

/**
 * Comparação de Produtos
 * Retorna comparação entre produtos normais, ponta e promoção
 */
export const getProductComparison = async (
  periodo: string = '30d',
  produtoId?: string
) => {
  const params: Record<string, any> = { periodo };
  if (produtoId) params.produto_id = produtoId;
  
  const response = await api.get('/analytics/produtos/comparacao', { params });
  return response.data;
};

/**
 * Produtos Especiais
 * Retorna produtos em ponta de estoque ou promoção
 */
export const getProdutosEspeciais = async (
  periodo: string = '30d',
  tipo?: 'ponta' | 'promocao' | 'normal',
  lojaId?: number,
  categoria?: string,
  vendedorId?: number
) => {
  const params: Record<string, any> = { periodo };
  if (tipo) params.tipo = tipo;
  if (lojaId) params.loja_id = lojaId;
  if (categoria) params.categoria = categoria;
  if (vendedorId) params.vendedor_id = vendedorId;
  
  const response = await api.get('/analytics/produtos-especiais', { params });
  return response.data;
};

export { api };
export default api;
